/**
 * /announcements/:announcementId — full-page card view for an
 * announcement (per-project or tenant-wide).
 *
 * Same id-based shape as `/events/:eventId`. Tenant-wide announcements
 * (`projectId === null`) render with empty per-project members /
 * statuses; the modal accepts that gracefully.
 */

import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'

import { getAccessToken } from '@/auth/cookie-helpers'
import { CardDetailPage, type CardPageSearch } from '@/features/board/components/CardDetailPage'

export const Route = createFileRoute('/announcements/$announcementId')({
  component: AnnouncementDeepLinkRoute,
  validateSearch: (search: Record<string, unknown>): CardPageSearch => ({
    commentId: typeof search.commentId === 'string' ? search.commentId : undefined,
    parentCommentId:
      typeof search.parentCommentId === 'string' ? search.parentCommentId : undefined,
  }),
})

function AnnouncementDeepLinkRoute() {
  const navigate = Route.useNavigate()
  const { commentId, parentCommentId } = Route.useSearch()
  const stripCommentId = useCallback(
    () =>
      navigate({
        search: (prev) => ({ ...prev, commentId: undefined, parentCommentId: undefined }),
        replace: true,
      }),
    [navigate],
  )
  const { announcementId } = Route.useParams()

  useEffect(() => {
    if (!getAccessToken()) {
      navigate({ to: '/login' })
    }
  }, [navigate])

  if (!getAccessToken()) return null

  return (
    <CardDetailPage
      kind="announcement"
      id={announcementId}
      commentId={commentId}
      parentCommentId={parentCommentId}
      onCommentIdConsumed={stripCommentId}
    />
  )
}
